import React, { useCallback, useEffect, useRef, useState } from "react";
import PageWrapper from "./PageWrapper";
import PairComparisonView from "./PairComparisonView";
import {
  getCommonBlockState,
  getNextCommonComparison,
  startCommonBlock,
  submitCommonBtrtAnswer,
} from "../service/api";
import { commonErrorMessage, newClientResponseId } from "./commonSubmission";

export default function CommonBtrtTask({ userId, initialCommonSessionId = null, onPhaseChange }) {
  const introKey = `commonMovieComparisonIntroDismissed:${userId}`;
  const [showIntro, setShowIntro] = useState(() => sessionStorage.getItem(introKey) !== "true");
  const [commonSessionId, setCommonSessionId] = useState(initialCommonSessionId);
  const [dto, setDto] = useState(null);
  const [answered, setAnswered] = useState(0);
  const [loading, setLoading] = useState(!showIntro);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(null);
  const decisionStartedAt = useRef(null);
  const comparisonHeadingRef = useRef(null);

  const recover = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let state = await getCommonBlockState({ userId, commonSessionId });
      let sid = state.commonSessionId;
      if (!sid) {
        const started = await startCommonBlock(userId);
        sid = started.commonSessionId;
        state = await getCommonBlockState({ userId, commonSessionId: sid });
      }
      setCommonSessionId(sid);
      setAnswered(state.btrt?.answered ?? 0);
      if (state.phase !== "common_btrt_in_progress") {
        onPhaseChange(state.phase, sid);
        return;
      }
      const next = await getNextCommonComparison({ userId, commonSessionId: sid });
      if (next.done) {
        onPhaseChange(next.phase, sid);
        return;
      }
      setDto(next);
      setRetry(null);
      if (next.answerable) decisionStartedAt.current = performance.now();
    } catch (requestError) {
      setError(commonErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [commonSessionId, onPhaseChange, userId]);

  useEffect(() => { if (dto?.answerable) comparisonHeadingRef.current?.focus(); }, [dto?.issuanceId, dto?.answerable]);

  useEffect(() => { if (!showIntro) recover(); }, [showIntro]); // eslint-disable-line react-hooks/exhaustive-deps

  const runSubmission = async (submission) => {
    setSubmitting(true);
    setError("");
    try {
      await submission.send(submission.payload);
      setRetry(null);
      await recover();
    } catch (requestError) {
      setRetry(submission);
      setError(commonErrorMessage(requestError));
      if ([403, 409].includes(requestError?.status)) await recover();
    } finally {
      setSubmitting(false);
    }
  };

  const submitAnswer = (rawAction, cannotJudgeReason = null) => {
    const reactionTimeMs = Math.max(0, Math.round(performance.now() - decisionStartedAt.current));
    runSubmission({
      kind: "answer",
      send: submitCommonBtrtAnswer,
      payload: {
        userId, commonSessionId, issuanceId: dto.issuanceId, answerToken: dto.answerToken,
        rawAction, cannotJudgeReason, reactionTimeMs, clientResponseId: newClientResponseId(),
      },
    });
  };

  if (showIntro) return (
    <PageWrapper>
      <section className="common-intro" style={{ textAlign: "center" }}>
        <h2>Movie comparisons</h2>
        <p>You will compare the same seven movies in pairs, one pair at a time.</p>
        <p>For each pair, please choose the movie you generally prefer. Think about your broader, long-term movie preferences rather than only what you happen to feel like watching right now.</p>
        <p>You do not need to have seen or recognized a movie before. For each movie, we will show you its title, poster, and other information to help you make an informed choice.</p>
        <p>If you genuinely have no preference between the two movies, you can indicate this instead of choosing one. Use this only if you truly have no preference in either direction.</p>
        <p>You will complete 21 comparisons. Afterwards, you will briefly tell us whether you had enough information to judge each movie and then arrange the seven movies from most to least preferred.</p>
        <p>There are no right or wrong answers.</p>
        <button type="button" className="submit-button" onClick={() => { sessionStorage.setItem(introKey, "true"); setShowIntro(false); }}>Start movie comparisons</button>
      </section>
    </PageWrapper>
  );
  if (loading && !dto) return <p role="status">Loading movie comparisons…</p>;
  if (!dto) return <div className="common-error" role="alert">{error}<button type="button" onClick={recover}>Try again</button></div>;

  return (
    <section>
      <h2 ref={comparisonHeadingRef} tabIndex="-1">Common movie comparisons</h2>
      <p className="common-progress">Pair {answered + 1} / 21</p>
      <PairComparisonView left={dto.left} right={dto.right} disabled={submitting} responseLocked={Boolean(retry)} error={error} onAction={submitAnswer} onRetry={retry ? () => runSubmission(retry) : null} />
    </section>
  );
}
